/**
 * The corrections staff have made to this workspace's **details**, in order,
 * with the reason each person gave.
 *
 * READ THE NEXT PARAGRAPH BEFORE CHANGING ANY COPY IN THIS FILE.
 *
 * **CREDIT GRANTS ARE NOT IN THIS LIST, AND THIS PANEL MUST NOT IMPLY THEY
 * ARE.** The route behind it — `GET /admin/tenants/:tenantId/audit` — reads
 * `admin_audit`, and nothing writes a `tenant.credits_granted` row there:
 * `AdminCreditsService.topup` runs its movement on the RLS-enforced `wend_app`
 * connection so a wrong tenant id cannot spend into the wrong workspace's
 * append-only ledger, `wend_app` holds nothing on `admin_audit` (0015 revokes
 * it), so the grant is audited into `audit_log` inside the movement's own
 * transaction instead. `admin-audit.service.ts` argues it in full and
 * `admin-credits.service.ts` names the migration that closes it.
 *
 * This panel shipped claiming the opposite, in three places at once: a heading
 * saying "everything staff have done to this workspace", a description saying
 * "every change staff have made", and an empty state reading "no staff member
 * has corrected this workspace **or granted it credits**" — on workspaces that
 * had been granted credits. That last sentence is the expensive one. It is a
 * *false negative* on the exact question the required reason exists to answer:
 * support asked "why is this balance 50,000?" opens this screen, is told nobody
 * granted anything, and goes looking for a bug in the ledger. Saying less is
 * cheap; saying something untrue in an audit surface is not.
 *
 * So the panel is named for what it holds, and says where the other half lives.
 * The `tenant.credits_granted` branch below stays, and is unreachable today:
 * `adminAuditRecordSchema` admits the shape, the route can filter for it, and
 * the `wend_credit_granter` role is the migration that starts producing it. A
 * renderer that is ready is not the same thing as copy that lies.
 *
 * THIS IS THE READER THE REQUIRED REASON WAS WRITTEN FOR — for the edit half.
 * The details form on this screen refuses to submit without an explanation, and
 * the argument for that is a question asked months later. A required reason with
 * no surface that reads it back does not answer that question; it only makes the
 * person typing it feel like it will be answered. The write and the read are one
 * feature — which is precisely why the half that has no reader yet is stated as
 * having none.
 *
 * THE TWO ROW SHAPES ARE RENDERED AS TWO ROW SHAPES. `adminAuditRecordSchema` is
 * a discriminated union on `action`: an edit carries the fields that moved, a
 * grant carries an amount, a channel, a ledger entry id and the balance either
 * side of the movement. Flattening both into one "what happened" line would
 * throw away the two facts a support conversation actually needs — which ledger
 * entry, and what the balance was immediately before it. The union is why that
 * flattening is not merely discouraged here but unavailable: there is no field
 * common to both that could hold it.
 *
 * ATTRIBUTION IS SHOWN AS THE EMAIL, NOT THE ID. `adminAuditActor` carries both
 * on purpose — a nullable pointer to the staff account and a copy of the email
 * taken at write time — because every audit foreign key in this schema is `ON
 * DELETE SET NULL`, so an offboarded staff member would otherwise erase their
 * own attribution from every row they ever wrote. The email is the field that
 * survives, so the email is what this panel reads. When the pointer has gone,
 * the row says so rather than silently implying the account is still live.
 *
 * A 404 IS REPORTED AS A 404 AND NOT AS "NO HISTORY". If the audit endpoint is
 * not reachable, this panel says the history could not be read — never that
 * there is none. An empty list and an unreachable list are different statements,
 * and the difference is exactly the one an audit trail exists to preserve: a
 * screen that renders "no changes recorded" when it in fact failed to ask is a
 * screen that has quietly given somebody the wrong answer to the only question
 * it is for.
 *
 * THE LIST IS AN EVENT LOG (slice 66). `@wend/ui`'s `EventLog` draws the rows:
 * the time in the left column, a small icon tile, a short title ("Details
 * changed", "Credits granted") and everything else as the row's detail. Every
 * fact the old rows carried is still in each row — who, when, each field's
 * before and after, the reason in the person's own words — and the two row
 * shapes are still two shapes, rendered by `UpdatedBody` and `GrantedBody`. The
 * kind used to be a pill beside the date; it is now the row's title, and the
 * icon tile carries the same distinction for the eye (a pencil for an edit, a
 * coin in violet for a grant). Neither is a status colour: an edit is not good
 * or bad news, so the `info` cyan the grant pill wore is gone.
 *
 * Inside the log the detail is set in spans, not `<dl>` and `<blockquote>`:
 * the log puts the detail in an inline slot, and a block element inside it is
 * invalid HTML that browsers repair differently. Each change is one line —
 * the field, the old value struck through, "becomes" for a screen reader where
 * the eye sees an arrow, the new value.
 */
import { Coins, PencilLine, RotateCw } from 'lucide-react';
import { useState } from 'react';

import type {
  AdminAuditRecord,
  TenantCreditsGrantedAuditRecord,
  TenantUpdatedAuditRecord,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EventLog,
  Spinner,
  type EventLogItem,
} from '@wend/ui';

import { describeApiError } from '../../lib/api-error';
import { listTenantAudit } from '../../lib/audit-api';
import { CHANNEL_LABEL } from '../../lib/credit-display';
import { formatCount, formatDateTime, formatRange } from '../../lib/format';
import { TENANT_FIELD_LABEL, formatAuditValue } from '../../lib/tenant-fields';
import { useAsyncData } from '../../lib/use-async-data';

/** Fewer than a list page: this is a sidebar-length history, not an archive. */
const AUDIT_PAGE_SIZE = 10;

export interface TenantAuditPanelProps {
  tenantId: string;
  /**
   * Bumped by the parent after a save or a grant, so the history re-reads
   * without the operator having to know it should.
   */
  reloadToken: number;
}

export function TenantAuditPanel({ tenantId, reloadToken }: TenantAuditPanelProps) {
  const [page, setPage] = useState(1);

  const { status, data, error, refreshing, reload } = useAsyncData(
    (signal) =>
      listTenantAudit({ tenantId, page, pageSize: AUDIT_PAGE_SIZE }, signal),
    [tenantId, page, reloadToken],
  );

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? null;
  const isFirstLoad = status === 'loading' && data === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1.02rem]">Tenant details changed by staff</CardTitle>
        <CardDescription>
          Corrections staff have made to this workspace&rsquo;s details, newest first, with the
          reason the person gave at the time. Rows are written by the API and cannot be edited or
          removed from here. <strong className="font-medium text-text">Credit top-ups are not
          listed here</strong> — a grant is recorded on this workspace&rsquo;s credit ledger, with
          its reason in the audit log, and no screen reads that back yet.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {isFirstLoad && (
          <div className="flex items-center gap-3 py-6 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading this workspace&rsquo;s change history…</span>
          </div>
        )}

        {!isFirstLoad && status === 'error' && error !== null && (
          <div role="alert" className="flex flex-col items-start gap-3">
            {/*
              Deliberately not the empty state. The history could not be read;
              that is not the same as there being none, and only one of those
              two sentences is safe to put in front of somebody investigating.
            */}
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
              This workspace&rsquo;s change history could not be read, so what follows is unknown
              rather than empty — there may well be changes recorded that are not shown here.{' '}
              {describeApiError(error)}
            </p>
            {error.requestId !== undefined && (
              <p className="font-mono text-[0.7rem] tracking-[0.06em] text-muted">
                Request {error.requestId}
              </p>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<RotateCw aria-hidden="true" />}
              onClick={reload}
            >
              Try again
            </Button>
          </div>
        )}

        {!isFirstLoad && status !== 'error' && items.length === 0 && (
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            {/*
              This sentence is about detail corrections and nothing else. It said
              "or granted it credits" and rendered that on workspaces that had
              been granted credits — see the file header. Provisioning is
              likewise recorded under a different action and is not listed here.
            */}
            No staff member has corrected this workspace&rsquo;s details. That says nothing about
            its credits: a top-up is not recorded in this list, so check the credit ledger before
            concluding none was granted.
          </p>
        )}

        {items.length > 0 && (
          <>
            <div className="flex items-center gap-3">
              {refreshing && <Spinner size="sm" label="Re-reading the history" />}
              {pagination !== null && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {formatRange(pagination.page, pagination.pageSize, pagination.total)}
                </p>
              )}
            </div>

            <EventLog label="Changes, newest first" whenWidth="date" items={items.map(auditItem)} />
          </>
        )}

        {pagination !== null && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => {
                setPage((current) => Math.max(1, current - 1));
              }}
            >
              Previous
            </Button>

            <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              Page {pagination.page} of {pagination.totalPages}
            </p>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => {
                setPage((current) => current + 1);
              }}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* One row                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * One record as a log row. The title is the kind; the detail holds the actor,
 * the change itself and the reason, in that order — the reason last and in the
 * text colour, because the reason is what this list exists to read back.
 */
function auditItem(record: AdminAuditRecord): EventLogItem {
  const granted = record.action === 'tenant.credits_granted';
  const [date, time] = splitDateTime(record.createdAt);

  return {
    key: record.id,
    when: (
      <time dateTime={record.createdAt} className="flex flex-col leading-snug">
        <span className="whitespace-nowrap">{date}</span>
        <span className="whitespace-nowrap">{time}</span>
      </time>
    ),
    tone: granted ? 'brand' : 'neutral',
    icon: granted ? <Coins aria-hidden="true" /> : <PencilLine aria-hidden="true" />,
    title: granted ? 'Credits granted' : 'Details changed',
    detail: (
      <span className="mt-1 flex flex-col gap-1.5">
        <Actor record={record} />
        {record.action === 'tenant.updated' ? <UpdatedBody record={record} /> : <GrantedBody record={record} />}
        <span className="border-l-2 border-line-strong pl-2.5 text-[0.84rem] leading-relaxed text-text">
          {record.reason}
        </span>
      </span>
    ),
  };
}

/** "17 Oct 2026, 10:42 am" as its date and its time, for the log's narrow time column. */
function splitDateTime(iso: string): readonly [string, string] {
  const full = formatDateTime(iso);
  const cut = full.lastIndexOf(', ');
  return cut === -1 ? [full, ''] : [full.slice(0, cut), full.slice(cut + 2)];
}

function Actor({ record }: { record: AdminAuditRecord }) {
  const { adminEmail, adminUserId } = record.actor;

  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
      <span className="font-mono text-[0.72rem] tracking-[0.04em] text-muted">{adminEmail}</span>
      {adminUserId === null && (
        // Stated rather than left blank: the account is gone, the attribution is
        // not. Silently showing only the email would imply the account is live.
        <span className="text-[0.72rem] text-muted">(staff account since removed)</span>
      )}
    </span>
  );
}

function UpdatedBody({ record }: { record: TenantUpdatedAuditRecord }) {
  return (
    <span className="flex flex-col gap-1">
      {record.changes.map((change) => (
        <span key={change.field} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
          <span className="text-[0.78rem] leading-6 text-muted sm:w-36 sm:shrink-0">
            {TENANT_FIELD_LABEL[change.field]}
          </span>
          <span className="min-w-0 text-[0.84rem] leading-6">
            <span className="text-muted line-through">
              {formatAuditValue(change.field, change.before, 'before')}
            </span>{' '}
            <span aria-hidden="true" className="text-brand-ink">
              →
            </span>
            <span className="sr-only"> becomes</span>{' '}
            <span className="break-words text-text">
              {formatAuditValue(change.field, change.after, 'after')}
            </span>
          </span>
        </span>
      ))}
    </span>
  );
}

function GrantedBody({ record }: { record: TenantCreditsGrantedAuditRecord }) {
  const label = CHANNEL_LABEL[record.channel];

  return (
    <span className="flex flex-col gap-1">
      <span className="text-[0.84rem] leading-relaxed text-text">
        <span className="tabular-nums">+{formatCount(record.amount)}</span> {label} credits.{' '}
        <span className="text-muted">
          Balance {formatCount(record.balanceBefore)} → {formatCount(record.balanceAfter)}.
        </span>
      </span>
      <span className="break-all font-mono text-[0.7rem] tracking-[0.04em] text-muted">
        ledger {record.ledgerEntryId} · key {record.idempotencyKey}
      </span>
    </span>
  );
}
